import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Camera, Upload, Loader2, Sparkles } from "lucide-react";
import { ReceiptReviewCard } from "@/components/ReceiptReviewCard";

export const ReceiptCapture = ({ categories, onSaved, highlight = false }) => {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [queue, setQueue] = useState([]); // {key, name, preview, status: reading|review|error, receipt, error}

  const process = async (files) => {
    const items = Array.from(files || []).map((f) => ({
      key: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`, name: f.name,
      preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null, status: "reading", file: f,
    }));
    if (!items.length) return;
    setQueue((q) => [...items, ...q]);
    for (const it of items) {
      const fd = new FormData();
      fd.append("file", it.file);
      try {
        const { data } = await api.post("/vault/receipts/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setQueue((q) => q.map((x) => (x.key === it.key ? { ...x, status: "review", receipt: data } : x)));
      } catch (e) {
        setQueue((q) => q.map((x) => (x.key === it.key ? { ...x, status: "error", error: e.response?.data?.detail || "Upload failed" } : x)));
      }
    }
    if (camRef.current) camRef.current.value = "";
    if (fileRef.current) fileRef.current.value = "";
  };

  const remove = (key) => setQueue((q) => q.filter((x) => x.key !== key));

  const reading = queue.filter((x) => x.status === "reading").length;

  return (
    <div data-testid="receipt-capture">
      <input ref={camRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
        data-testid="vault-camera-input" onChange={(e) => process(e.target.files)} />
      <input ref={fileRef} type="file" accept="image/*,.pdf" multiple className="hidden"
        data-testid="vault-file-input" onChange={(e) => process(e.target.files)} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Button onClick={() => camRef.current?.click()} data-testid="vault-snap-btn"
          className={`rounded-2xl bg-[#114A31] hover:bg-[#1A6B46] text-white h-auto py-5 flex-col gap-1.5 ${highlight ? "ring-4 ring-[#7FE3AE] ring-offset-2 animate-pulse" : ""}`}>
          <Camera className="h-6 w-6" />
          <span className="font-semibold">Snap a receipt</span>
          <span className="text-xs text-white/70 font-normal">Opens your camera · multi-shot</span>
        </Button>
        <Button onClick={() => fileRef.current?.click()} variant="outline" data-testid="vault-upload-btn"
          className="rounded-2xl border-dashed border-[#114A31] text-[#114A31] hover:bg-[#E6F0EB] h-auto py-5 flex-col gap-1.5">
          <Upload className="h-6 w-6" />
          <span className="font-semibold">Upload photos or PDFs</span>
          <span className="text-xs text-[#595D5A] font-normal">From your phone or computer</span>
        </Button>
      </div>
      {reading > 0 && (
        <p className="mt-3 text-sm text-[#114A31] flex items-center gap-2" data-testid="vault-reading">
          <Loader2 className="h-4 w-4 animate-spin" /> <Sparkles className="h-4 w-4" /> Reading {reading} receipt{reading === 1 ? "" : "s"} with AI…
        </p>
      )}
      {queue.length > 0 && (
        <div className="mt-4 space-y-3" data-testid="vault-review-queue">
          {queue.map((it) => (
            <ReceiptReviewCard key={it.key} item={it} categories={categories}
              onDone={(saved) => { remove(it.key); if (saved) onSaved?.(); }} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ReceiptCapture;
